import { useState } from 'react';
import { Button, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shigo-ui/ui';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { dashboardT } from '../../../../lib/messages';
import { TargetingSupplyType } from '../../model';
import {
  supplyAppSelectionSchema,
  supplyDomainSelectionSchema,
  type SupplyAppSelection,
  type SupplyDomainSelection,
  type TailoredSupplySelection,
} from '../../../campaigns/supply/model';
import { AppsLiteTable } from './SelectedAppsTable';
import { DomainsLiteTable } from './SelectedDomainsTable';
import { ManagedSelectionTable } from './ManagedSelectionTable';
import { managedSelectionTableClassNames } from './ManagedSelectionTable.styles';
import { itemId, itemLabel, toRecords, type UnknownItem } from './SelectedSupplyTables.helpers';

export function SelectedTailoredSupplyTable({
  excludedApps,
  excludedDomains,
  items,
  supplyType,
  onExcludedAppsChange,
  onExcludedDomainsChange,
}: {
  excludedApps: SupplyAppSelection[];
  excludedDomains: SupplyDomainSelection[];
  items: TailoredSupplySelection[];
  supplyType: TargetingSupplyType;
  onExcludedAppsChange: (items: SupplyAppSelection[]) => void;
  onExcludedDomainsChange: (items: SupplyDomainSelection[]) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const tailoredSupplies = toRecords(items).filter(hasStableItemId);

  return (
    <ManagedSelectionTable aria-label={dashboardT('targeting.tailoredSupply')}>
      <TableHeader className={managedSelectionTableClassNames.header}>
        <TableRow className={managedSelectionTableClassNames.row}>
          <TableHead className={managedSelectionTableClassNames.head}>
            {dashboardT('madBuy.common.name')}
          </TableHead>
          <TableHead className={`${managedSelectionTableClassNames.head} w-12 text-right`}>
            {' '}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {tailoredSupplies.map((item) => {
          const id = itemId(item);
          const expanded = expandedId === id;
          return (
            <TailoredSupplyRows
              key={id}
              expanded={expanded}
              item={item}
              supplyType={supplyType}
              excludedApps={excludedApps}
              excludedDomains={excludedDomains}
              onExpandedChange={() => {
                setExpandedId(expanded ? null : id);
              }}
              onExcludedAppsChange={(items) => {
                onExcludedAppsChange(supplyAppSelectionSchema.array().parse(items));
              }}
              onExcludedDomainsChange={(items) => {
                onExcludedDomainsChange(supplyDomainSelectionSchema.array().parse(items));
              }}
            />
          );
        })}
      </TableBody>
    </ManagedSelectionTable>
  );
}

function TailoredSupplyRows({
  expanded,
  excludedApps,
  excludedDomains,
  item,
  supplyType,
  onExpandedChange,
  onExcludedAppsChange,
  onExcludedDomainsChange,
}: {
  expanded: boolean;
  excludedApps: unknown[];
  excludedDomains: unknown[];
  item: UnknownItem;
  supplyType: TargetingSupplyType;
  onExpandedChange: () => void;
  onExcludedAppsChange: (items: unknown[]) => void;
  onExcludedDomainsChange: (items: unknown[]) => void;
}) {
  const apps = toRecords(item['apps']);
  const domains = toRecords(item['domains']);

  return (
    <>
      <TableRow className={managedSelectionTableClassNames.row}>
        <TableCell className={`${managedSelectionTableClassNames.cell} font-medium`}>
          {itemLabel(item)}
        </TableCell>
        <TableCell className={`${managedSelectionTableClassNames.cell} text-right`}>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={dashboardT(expanded ? 'targeting.collapseNamed' : 'targeting.expandNamed', {
              name: itemLabel(item),
            })}
            onClick={onExpandedChange}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </TableCell>
      </TableRow>
      {expanded ? (
        <TableRow className={managedSelectionTableClassNames.row}>
          <TableCell colSpan={2} className="bg-muted/20 p-4">
            <div className="grid gap-4">
              {supplyType === TargetingSupplyType.Apps ? (
                <div className="grid gap-2">
                  <h4 className="text-foreground text-sm font-semibold">
                    {dashboardT('madBuy.common.apps')}
                  </h4>
                  <AppsLiteTable
                    items={apps}
                    selectable
                    excludedItems={excludedApps}
                    onExcludedItemsChange={onExcludedAppsChange}
                  />
                </div>
              ) : null}
              {supplyType === TargetingSupplyType.Domains ? (
                <div className="grid gap-2">
                  <h4 className="text-foreground text-sm font-semibold">
                    {dashboardT('madBuy.common.domains')}
                  </h4>
                  <DomainsLiteTable
                    items={domains}
                    selectable
                    excludedItems={excludedDomains}
                    onExcludedItemsChange={onExcludedDomainsChange}
                  />
                </div>
              ) : null}
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

function hasStableItemId(item: UnknownItem) {
  return typeof item.id === 'string' && item.id.trim().length > 0;
}
